import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { RestaurantService } from '../restaurant.service';
import { TraceTransportService } from './trace-transport.service';
import { OrderFlowSessionService } from './order-flow-session.service';
import { redact, FieldSpec } from './trace-redactor';
import { hiveTs, sampleBucket, inSample } from './trace-utils';

/**
 * MpBannerTraceService
 *
 * Emitter do banner "Developed by Multipedidos" do rodapé do cardápio →
 * stack SAM `mp-banner-monitor` (dataset `mp_banner_monitor.traces`). Também
 * do crédito "Desenvolvido com ❤️ pela Multipedidos" das telas de
 * acompanhamento, Informações, Horários e Pagamento: mesmo destino e mesmos
 * UTMs, em steps próprios (`Credit*`).
 *
 * ## Por que um pipeline próprio, e não o funil do pedido
 *
 * A primeira versão disto morava no `OrderFlowTraceService`. Não coube, e o
 * sintoma foi ter de abrir DUAS exceções ao que aquele serviço tem de mais
 * central: furar o gate de coorte, e quebrar a simetria de amostragem que o
 * `sampleKey` existe para proteger. Quando é preciso excetuar o invariante
 * nuclear de um sistema para caber nele, geralmente é porque não cabe.
 *
 * O argumento a favor do reuso era a correlação — mesmo `correlation_id`, logo
 * "quem clicou no banner enviou pedido?" seria uma query só. Ele é mais fraco
 * do que parece: o clique vale para a FROTA e o funil só existe para a COORTE,
 * então para a maioria esmagadora dos cliques o resto do funil simplesmente
 * não está lá. O que se ganhava de verdade era não provisionar infra — e o
 * preço era um dataset com duas populações misturadas, defendido só por
 * documentação.
 *
 * O `correlation_id` continua sendo emitido (mesmo `OrderFlowSessionService`),
 * então o JOIN entre os dois datasets segue possível no Athena para a fatia em
 * que ambos existem. Só deixou de ser o argumento que decide a arquitetura.
 *
 * ## O que muda em relação ao funil
 *
 *  - **População:** frota inteira. Não há gate de coorte aqui — é a razão de
 *    ser do dataset ("quais slugs geram mais cliques" não sobrevive a um
 *    ranking tirado das 525 lojas curadas).
 *  - **IP:** injetado pelo servidor em TODO evento (o navegador não sabe o
 *    próprio IP). Decisão de privacidade da stack inteira, tomada uma vez.
 *  - **Amostragem:** só a impressão é amostrada. Ver `inViewSample()`.
 *
 * O mecanismo (transporte, gates de ambiente, ts Hive, redação) é o MESMO do
 * funil, reusado: `TraceTransportService`, `trace-redactor`, `trace-utils`.
 * Isso é do app, não da stack — não custa nada e mantém uma convenção só.
 *
 * Fire-and-forget, sempre: nada aqui pode derrubar nem atrasar o cardápio.
 */

/**
 * Bump a cada mudança no que é emitido — ou no que é MEDIDO. Separa build velha
 * em cache de "ninguém clica", e é o que permite comparar duas builds na MESMA
 * janela de dias em vez de antes×depois no tempo (ver `delta-upgrade-via-client-code-version`).
 *
 * v2 (21/09/2026): o texto do botão virou um só para todo mundo (o vencedor do
 * A/B que ninguém tinha colhido) e o destino também — as duas landings do A/B
 * deixaram de existir, então `variant` saiu do payload. Misturar as duas
 * populações apagaria exatamente o efeito que se quer conferir.
 */
export const MP_BANNER_CODE_VERSION = 'mp-banner-v2';

/**
 * Fração de devices que reportam a IMPRESSÃO do banner.
 *
 * O clique NÃO é amostrado: um ranking por loja não sobrevive a 10% dos
 * devices, e um clique de rodapé é evento raro — o volume é irrelevante. A
 * impressão é: a 100% ela somaria, sozinha, volume da ordem de um pageview de
 * cardápio de toda a base, para responder algo que 10% respondem com folga.
 *
 * A assimetria só é legítima por causa do `in_view_sample` que o clique carrega
 * (ver `inViewSample`). Mudar este número muda o carimbo junto, sem ninguém
 * precisar lembrar.
 */
export const BANNER_VIEW_SAMPLE_PCT = 10;

/**
 * Allowlist do `data` de cada step — mesmo mecanismo default-deny da taxonomia
 * do funil: campo não declarado não é emitido, e o contador `_dropped` acende.
 *
 * Impressão e clique compartilham o bloco de contexto que o componente monta,
 * então esta allowlist é a UNIÃO dos dois. Uma divergência aqui não quebra
 * nada visível — só apaga um campo em silêncio, que foi exatamente o erro que
 * o E2E pegou na primeira versão (`scrolled` faltando).
 */
const BANNER_FIELDS: FieldSpec = {
  // Identificação da peça e da loja.
  // `slug` é `restaurants.restaurant_url`, identificador PÚBLICO da loja (está
  // na URL que o consumidor digita) — não é dado de pessoa. É a chave da
  // pergunta do marketing, e o `utm_campaign` do link carrega o mesmo valor.
  slug: 'raw',
  slot: 'raw',
  // `variant` saiu em 21/09/2026 junto com o A/B de landing. O dado histórico
  // (v1) segue tendo o campo; da v2 em diante ele não existe mais.

  // "Em que etapa da página": o rodapé pode ser alcançado por rolagem longa
  // (cardápio grande) ou já nascer visível (cardápio de 3 itens), e os dois
  // dizem coisas opostas sobre atenção.
  scroll_pct: 'num',
  scrolled: 'bool',
  ms_on_page: 'num',
  ms_since_view: 'num',

  // Contexto de negócio. A hipótese óbvia é que loja fechada empurra para o
  // banner; sem estes campos ela não é testável.
  cart_items: 'num',
  cart_total: 'money',
  is_closed: 'bool',
  cashier_is_open: 'bool',

  // "Quais dispositivos". O `user_agent` cru vai no envelope; estes são o que
  // o UA não conta e o que dá para agrupar sem parser.
  viewport_w: 'num',
  viewport_h: 'num',
  dpr: 'num',
  standalone: 'bool',
  lang: 'raw',

  // Só no clique.
  in_view_sample: 'bool',
  target_url: 'raw',
};

/**
 * Versão do crédito "Desenvolvido com ❤️ pela Multipedidos". Própria, e não a
 * do banner: as duas peças mudam em momentos diferentes, e subir `mp-banner-v2`
 * por causa do crédito tiraria do ar as queries do banner, que filtram por ela,
 * sem que nada nele tivesse mudado.
 *
 * Antes desta, `track-credit-v1` (17/10/2026, só o acompanhamento, com os
 * steps `TrackCreditViewed`/`TrackCreditClicked`). v1 (17/10/2026): o crédito
 * chega a Informações, Horários e Pagamento, e os steps viram `Credit*` com a
 * tela em `screen`.
 */
export const MP_CREDIT_CODE_VERSION = 'mp-credit-v1';

/**
 * Allowlist do crédito. Menor que a do banner de propósito: uma das telas é a
 * do PEDIDO — a URL do acompanhamento carrega o token dele e o servidor
 * acrescenta o IP a todo evento desta stack —, então nada do pedido (uuid,
 * token, status, itens) nem da URL entra aqui. O default-deny garante que
 * entrar exigiria declarar o campo nesta lista.
 */
const CREDIT_FIELDS: FieldSpec = {
  slug: 'raw',
  // `track` | `info` | `hours` | `payments` — o mesmo valor do `utm_content`.
  screen: 'raw',

  // `ms_since_view = -1` no clique é a assinatura de clique sem gente: o
  // crédito nunca apareceu na tela (no banner, foi a pré-carga do webview da
  // Meta — ver TELEMETRIA-BANNER-RODAPE.md). Mesma régua do banner, sem os
  // campos de rolagem: aqui quem rola é o corpo da tela, não a janela.
  ms_on_page: 'num',
  ms_since_view: 'num',

  viewport_w: 'num',
  viewport_h: 'num',
  dpr: 'num',
  standalone: 'bool',
  lang: 'raw',

  // Só no clique.
  target_url: 'raw',
};

const STEPS: { [step: string]: { fields: FieldSpec; version: string } } = {
  BannerViewed: { fields: BANNER_FIELDS, version: MP_BANNER_CODE_VERSION },
  BannerClicked: { fields: BANNER_FIELDS, version: MP_BANNER_CODE_VERSION },
  // Nomes próprios, e não `BannerViewed` com outro `slot`: as queries e o
  // detector do banner contam pelo `step`, e o crédito entraria nos números
  // do rodapé do cardápio.
  CreditViewed: { fields: CREDIT_FIELDS, version: MP_CREDIT_CODE_VERSION },
  CreditClicked: { fields: CREDIT_FIELDS, version: MP_CREDIT_CODE_VERSION },
};

@Injectable({ providedIn: 'root' })
export class MpBannerTraceService {
  constructor(
    private restaurantService: RestaurantService,
    private session: OrderFlowSessionService,
    private transport: TraceTransportService,
  ) { }

  private info(): any {
    try {
      return (this.restaurantService.restaurant && this.restaurantService.restaurant.info) || {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Este device reporta impressões?
   *
   * Público porque o NUMERADOR precisa carimbar se pertence à população do
   * DENOMINADOR: é `out.in_view_sample` do clique, e é a única coisa que torna
   * a taxa calculável apesar de a impressão ser amostrada e o clique não.
   *
   * A alternativa seria multiplicar por 10 na query — um fator de correção que
   * vive fora do dado, que alguém esquece, e cujo resultado errado sai
   * plausível. Este carimbo não tem como ser esquecido: ou está na linha, ou a
   * linha é de uma build anterior.
   */
  inViewSample(): boolean {
    return inSample(this.session.deviceId + ':mp-banner', BANNER_VIEW_SAMPLE_PCT);
  }

  /** Impressão do banner — amostrada. */
  emitViewed(payload: any, caller = ''): void {
    if (!this.inViewSample()) return;
    this.emit('BannerViewed', payload, caller, false);
  }

  /**
   * Clique no banner — frota inteira, sem amostragem.
   *
   * `beacon`: o clique leva a pessoa para outro site. O `HttpClient` morre
   * junto com o documento; o beacon é entregue pelo browser mesmo após o
   * unload. Não cancelamos a navegação — o `<a href>` segue seu curso, e
   * trocar isso por navegação via JS faria o link depender de a telemetria não
   * falhar.
   */
  emitClicked(payload: any, caller = ''): void {
    this.emit('BannerClicked', payload, caller, true);
  }

  /**
   * O crédito apareceu na tela — frota inteira, sem amostra.
   *
   * Ao contrário da impressão do banner, esta cabe inteira: sai no máximo uma
   * vez por visita a uma tela que a pessoa abre de propósito (acompanhamento,
   * Informações, Horários, Pagamento) — volume da ordem dos pedidos, não dos
   * pageviews de cardápio. Com impressão e clique na mesma população, a taxa é
   * a divisão simples, sem carimbo.
   */
  emitCreditViewed(payload: any, caller = ''): void {
    this.emit('CreditViewed', payload, caller, false);
  }

  /**
   * Clique no crédito. Beacon mesmo abrindo em outra aba: no celular, o
   * navegador pode congelar a aba de trás assim que a nova abre.
   */
  emitCreditClicked(payload: any, caller = ''): void {
    this.emit('CreditClicked', payload, caller, true);
  }

  private emit(step: string, payload: any, caller: string, beacon: boolean): void {
    try {
      // Sem endpoint configurado, não há telemetria. É o gate que separa os
      // ambientes: green/blue/beta têm `production: true` e apontam para
      // backends próprios — se emitissem, poluiriam o dataset real.
      if (!environment.mpBannerLogURL) return;
      if (!this.transport.canEmit()) return;

      const info = this.info();
      const rid = Number(info.id) || 0;
      // Sem restaurante não há a dimensão que dá sentido ao evento.
      if (!rid) return;

      const r = redact(payload, STEPS[step].fields);

      const data: any = {
        ...r.value,
        // Sinal de allowlist desatualizada: campo que o app manda e ninguém
        // declarou. -1 = payload patológico (circular/getter que lança).
        _dropped: r.dropped,
        device_id: this.session.deviceId,
        sample_bucket: sampleBucket(this.session.deviceId),
        // Elo com o funil do pedido (outra stack, outro dataset): permite o
        // JOIN no Athena para a fatia em que a loja também está na coorte.
        // Deixou de ser argumento de arquitetura, continua sendo útil de graça.
        correlation_id: this.session.attemptId,
        triggered_via_caller_explicit: caller,
        user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '').slice(0, 200),
        client_code_version: STEPS[step].version,
        // `client_ip` é acrescentado pelo SERVIDOR (ingest da stack). O
        // navegador não sabe o próprio IP — e um campo aqui seria forjável.
      };

      const envelope = {
        ts: hiveTs(),
        source: 'pedirdelivery-client',
        step,
        restaurant_id: String(rid),
        // Coluna de topo no Glue: é a dimensão do `GROUP BY` mais comum, e
        // assim ele não paga `json_extract_scalar`.
        slug: String(info.restaurant_url || ''),
        data: JSON.stringify(data),
      };

      if (beacon) {
        this.transport.sendBeacon(environment.mpBannerLogURL, envelope);
      } else {
        this.transport.send(environment.mpBannerLogURL, envelope);
      }
    } catch (e) {
      // Telemetria nunca pode derrubar o cardápio — o erro morre aqui.
      //
      // Mas morrer em silêncio também em DEV/E2E esconderia um emitter
      // quebrado até alguém estranhar o dataset vazio semanas depois.
      if (!environment.production || this.transport.isCypressRun()) {
        // eslint-disable-next-line no-console
        console.error('[mp-banner] emit falhou —', step, e);
      }
    }
  }
}
